import type { MedplumClient } from '@medplum/core'
import type { Practitioner } from '@medplum/fhirtypes'

export const SIGNATURE_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/practitioner-signature'

/**
 * Saves a signature PNG data URL as a Binary resource and adds/updates
 * the Practitioner extension to reference it.
 */
export async function saveSignatureToPractitioner(
  medplum: MedplumClient,
  practitionerId: string,
  signatureDataUrl: string,
): Promise<void> {
  // Convert data URL to Blob for createBinary
  const base64Data = signatureDataUrl.replace(/^data:image\/png;base64,/, '')
  const byteArray = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))
  const blob = new Blob([byteArray], { type: 'image/png' })

  // Upload as Binary resource
  const binary = await medplum.createBinary({
    data: blob,
    contentType: 'image/png',
  })

  // Read latest version of Practitioner to get current versionId for optimistic locking
  const practitioner = await medplum.readResource('Practitioner', practitionerId)

  // Update Practitioner with extension referencing the Binary
  const existingExtensions = (practitioner.extension || []).filter(
    (ext) => ext.url !== SIGNATURE_EXTENSION_URL,
  )

  await medplum.updateResource<Practitioner>({
    ...practitioner,
    extension: [
      ...existingExtensions,
      {
        url: SIGNATURE_EXTENSION_URL,
        valueReference: {
          reference: `Binary/${binary.id}`,
          display: 'Practitioner Signature',
        },
      },
    ],
  })
}

/**
 * Retrieves the saved signature as a data URL from a Practitioner's extension.
 * Uses medplum.download() which handles environment-aware URL construction
 * (localhost, staging, prod) and authentication automatically.
 * Returns null if no signature is saved.
 */
export async function loadSavedSignature(
  medplum: MedplumClient,
  practitioner: Practitioner,
): Promise<string | null> {
  const sigExtension = practitioner.extension?.find((ext) => ext.url === SIGNATURE_EXTENSION_URL)

  if (!sigExtension?.valueReference?.reference) {
    return null
  }

  try {
    const binaryRef = sigExtension.valueReference.reference // "Binary/xxx"
    const blob = await medplum.download(binaryRef)
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (err) {
    console.warn('[Signature] Failed to read signature:', err)
    return null
  }
}
